import { useEffect, useMemo, useRef, useState } from 'react';
import type { Issue, Person, Scenario } from '../game/types';
import { BoardColumn } from './BoardColumn';

interface Props {
  scenario: Scenario;
  flagged: ReadonlySet<string>;
  onToggleFlag: (kind: 'issue' | 'column' | 'sprint', id: string) => void;
  onOpenDetail: (issueKey: string) => void;
}

export function Board({ scenario, flagged, onToggleFlag, onOpenDetail }: Props) {
  const boardRef = useRef<HTMLDivElement>(null);
  const [activeColumn, setActiveColumn] = useState(scenario.columns[0]?.id ?? '');

  const issuesByKey = useMemo(() => {
    const map = new Map<string, Issue>();
    for (const issue of scenario.issues) map.set(issue.key, issue);
    return map;
  }, [scenario]);

  const peopleById = useMemo(() => {
    const map = new Map<string, Person>();
    for (const person of scenario.people) map.set(person.id, person);
    return map;
  }, [scenario]);

  /**
   * Sprint totals are derived from the tickets, never stored. If a scenario
   * author changes a card's points the header follows automatically, so the
   * board can't display a number the cards contradict.
   */
  const committed = useMemo(
    () =>
      scenario.issues
        .filter((i) => i.sprint === scenario.sprint.name)
        .reduce((sum, i) => sum + (i.points ?? 0), 0),
    [scenario],
  );

  const completed = useMemo(() => {
    const doneColumn = scenario.columns.find((c) => c.isDone);
    if (!doneColumn) return 0;
    return doneColumn.issueKeys.reduce(
      (sum, key) => sum + (issuesByKey.get(key)?.points ?? 0),
      0,
    );
  }, [scenario, issuesByKey]);

  /**
   * Keep the mobile chip nav in step with which column is actually on screen.
   *
   * Derived from scroll position rather than an IntersectionObserver: the
   * observer only reports entries whose visibility *changed*, so any layout
   * shift -- such as an overlay closing -- can leave it latched onto a column
   * that is nowhere near the viewport. Measuring directly is deterministic and
   * matches scroll-snap-align:start exactly.
   */
  useEffect(() => {
    const root = boardRef.current;
    if (!root) return;

    const syncActiveColumn = () => {
      const rootLeft = root.getBoundingClientRect().left;
      let best: HTMLElement | undefined;
      let bestDistance = Number.POSITIVE_INFINITY;

      for (const column of root.querySelectorAll<HTMLElement>('.column')) {
        const distance = Math.abs(column.getBoundingClientRect().left - rootLeft);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = column;
        }
      }

      if (best) setActiveColumn(best.id.replace(/^col-/, ''));
    };

    syncActiveColumn();
    root.addEventListener('scroll', syncActiveColumn, { passive: true });
    window.addEventListener('resize', syncActiveColumn);
    return () => {
      root.removeEventListener('scroll', syncActiveColumn);
      window.removeEventListener('resize', syncActiveColumn);
    };
  }, [scenario]);

  const jumpToColumn = (id: string) => {
    document
      .getElementById(`col-${id}`)
      ?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  };

  const sprintFlagged = flagged.has(`sprint:${scenario.sprint.name}`);
  const overCommitted = committed > scenario.sprint.velocity;

  return (
    <>
      <button
        type="button"
        className={`sprint${sprintFlagged ? ' is-flagged' : ''}`}
        aria-pressed={sprintFlagged}
        aria-label={`${scenario.sprint.name}. Committed ${committed} points against a velocity of ${scenario.sprint.velocity}. ${
          sprintFlagged ? 'Flagged.' : 'Not flagged.'
        } Activate to toggle.`}
        onClick={() => onToggleFlag('sprint', scenario.sprint.name)}
      >
        {sprintFlagged && (
          <span className="flag-marker" aria-hidden="true">
            ⚑
          </span>
        )}
        <div className="sprint__row">
          <span className="sprint__name">{scenario.sprint.name}</span>
          <span className="sprint__days">
            {scenario.sprint.daysRemaining} days remaining
          </span>
        </div>
        <div className="sprint__goal">{scenario.sprint.goal}</div>
        <div className="sprint__stats">
          <span>
            <span className="stat__label">Committed</span>
            <span
              className="stat__value"
              style={overCommitted ? { color: 'var(--red)' } : undefined}
            >
              {committed}
            </span>
          </span>
          <span>
            <span className="stat__label">Velocity</span>
            <span className="stat__value">{scenario.sprint.velocity}</span>
          </span>
          <span>
            <span className="stat__label">Completed</span>
            <span className="stat__value">{completed}</span>
          </span>
        </div>
      </button>

      {/* Phone-only column nav. Hidden at desktop widths where every column is
          already visible at once. */}
      <nav className="chipbar" aria-label="Jump to column">
        {scenario.columns.map((column) => (
          <button
            key={column.id}
            type="button"
            className="chip"
            aria-current={activeColumn === column.id}
            onClick={() => jumpToColumn(column.id)}
          >
            {column.name}
            <span className="chip__count">{column.issueKeys.length}</span>
          </button>
        ))}
      </nav>

      <div className="board" ref={boardRef}>
        {scenario.columns.map((column) => (
          <BoardColumn
            key={column.id}
            column={column}
            issues={column.issueKeys
              .map((key) => issuesByKey.get(key))
              .filter((i): i is Issue => i !== undefined)}
            peopleById={peopleById}
            flagged={flagged}
            currentSprint={scenario.sprint.name}
            onToggleFlag={onToggleFlag}
            onOpenDetail={onOpenDetail}
          />
        ))}
      </div>
    </>
  );
}
